import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";
import { AppRoutes } from "../../../routes/AppRoutes";
import { RetentionOffer } from "../../../types/rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";
import { formatPrice } from "../../../helpers/currencyHelper";
import { formatDate } from "../../../helpers/formatDate";
import { getSubscriptionTitle } from "../../../helpers/getSubscriptionTitle";

import LogoSvg from "../../atoms/LogoSvg/LogoSvg";
import SUCCESS from "../../../assets/images/SUCCESS.svg";
import SAD from "../../../assets/images/SAD.svg";
import BALLOON from "../../../assets/images/BALLOON.svg";
import FAIL from "../../../assets/images/FAIL.svg";
import Text from "../../atoms/Text/Text";
import Title from "../../atoms/Title/Title";
import Footer from "../../templates/Footer/Footer";
import Header from "../../templates/Header/Header";
import StyledButton from "../../atoms/StyledButton/StyledButton";

const ManageSubscriptionEditResult = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithParams();
  const location = useLocation();
  const context: string = location.state?.context;

  const getContent = () => {
    if (context === 'cancellation_success') {
      return {
        image: SAD,
        title: t("subscription:MANAGE_PAGE.EDIT_RESULT.CANCELLATION_SUCCESS.TITLE"),
        subtitle: location.state?.type === 'subscription'
          ? t("subscription:MANAGE_PAGE.EDIT_RESULT.CANCELLATION_SUCCESS.SUBSCRIPTION.SUBTITLE")
          : t("subscription:MANAGE_PAGE.EDIT_RESULT.CANCELLATION_SUCCESS.OPTION.SUBTITLE", {
            option_title: location.state?.code === 'warranty'
              ? t("subscription:MANAGE_PAGE.ACCOUNT_OPTIONS.WARRANTY.TITLE")
              : location.state?.code === 'premium_pack'
                ? t("subscription:MANAGE_PAGE.ACCOUNT_OPTIONS.PREMIUM_PACK.TITLE")
                : null
          })
      };
    }

    if (context === 'offer_success') {
      const offer: RetentionOffer = location.state?.offer;
      const next_charge_at: string = location.state?.next_charge_at;
      if (offer?.offer_type === 'upgrade_promo') {
        return {
          image: SUCCESS,
          title: t("subscription:MANAGE_PAGE.EDIT_RESULT.OFFER_SUCCESS.UPGRADE.TITLE"),
          subtitle: t("subscription:MANAGE_PAGE.EDIT_RESULT.OFFER_SUCCESS.UPGRADE.SUBTITLE", {
            tracker_name: location.state?.tracker_name ?? '',
            update_offer_title: offer.upgrade_period != null ? getSubscriptionTitle(offer.upgrade_period) : '',
            upgrade_amount: formatPrice(offer.upgrade_amount! / 100, offer.upgrade_currency!),
            next_charge_at: formatDate(next_charge_at),
          }),
        };
      }
      if (offer?.offer_type === 'free_months') {
        return {
          image: SUCCESS,
          title: t("subscription:MANAGE_PAGE.EDIT_RESULT.OFFER_SUCCESS.FREE_MONTHS.TITLE"),
          subtitle: t("subscription:MANAGE_PAGE.EDIT_RESULT.OFFER_SUCCESS.FREE_MONTHS.SUBTITLE", {
            tracker_name: location.state?.tracker_name ?? '',
            free_months: offer.free_months,
            next_charge_at_with_free_months: formatDate((() => {
              const d = new Date(location.state?.next_charge_at);
              d.setMonth(d.getMonth() + (offer.free_months ?? 0));
              return d.toISOString();
            })()),
          }),
        };
      }
      if (offer?.offer_type === 'create_ticket') {
        return {
          image: BALLOON,
          title: t("subscription:MANAGE_PAGE.EDIT_RESULT.OFFER_SUCCESS.CREATE_TICKET.TITLE"),
          subtitle: t("subscription:MANAGE_PAGE.EDIT_RESULT.OFFER_SUCCESS.CREATE_TICKET.SUBTITLE"),
        };
      }
      return {};
    }

    if (context === 'success') {
      const new_period: number = location.state?.new_period;
      const new_amount: number = location.state?.new_amount;
      const new_currency: string = location.state?.new_currency;
      const next_charge_at: string = location.state?.next_charge_at;

      return {
        image: SUCCESS,
        title: t("subscription:MANAGE_PAGE.EDIT_RESULT.CHANGE_SUBSCRIPTION.SUCCESS.TITLE"),
        subtitle: t("subscription:MANAGE_PAGE.EDIT_RESULT.CHANGE_SUBSCRIPTION.SUCCESS.SUBTITLE", {
          update_offer_title: new_period != null ? getSubscriptionTitle(new_period) : '',
          upgrade_amount: formatPrice(new_amount / 100, new_currency),
          next_charge_at: formatDate(next_charge_at),
        }),
      };
    }

    return {
      image: FAIL,
      title: t("subscription:MANAGE_PAGE.EDIT_SUBSCRIPTION.ERROR.TITLE"),
      subtitle: t("subscription:MANAGE_PAGE.EDIT_SUBSCRIPTION.ERROR.SUBTITLE"),
    };
  };

  const { image = SUCCESS, title, subtitle } = getContent();

  return (
    <>
      <Header
        headerStep={{ context: "manage" }}
        title={t("subscription:MANAGE_PAGE.EDIT_SUBSCRIPTION.HEADER")}
      />
      <Box className="ManageSubscriptionEditResult">
        <Box className="content">
          <LogoSvg className="Logos" src={image} />
          <Title variant="h1">{title}</Title>
          <Text>{subtitle}</Text>
        </Box>
      </Box>
      <Footer headerStep={{ context: "manage" }}>
        <StyledButton
          children={t("subscription:MANAGE_PAGE.EDIT_RESULT.BUTTON")}
          onClick={() => navigate(AppRoutes.MANAGE_SUBSCRIPTIONS)}
        />
      </Footer>
    </>
  );
};

export default ManageSubscriptionEditResult;
